/**
 * ConversationDrawerPanel — BUB-135
 * Loads and renders the full Paul ↔ customer chat for a conversation that is not
 * yet linked to an OS lead (keyed by customer phone / channel id).
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Dropdown,
  Flex,
  Grid,
  Modal,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CloseCircleOutlined,
  MoreOutlined,
  ReloadOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import OutboundMessageButton from "../../../shared/components/OutboundMessageButton.jsx";
import { loadOpsChannelsFromSettings } from "../../../shared/utils/outboundChannelLoaders.js";
import { OPS_OUTBOUND_CHANNEL_STORAGE_KEY } from "../../../shared/utils/outboundMessaging.js";
import { useAuth } from "../../../contexts/AuthContext.jsx";
import { usePublicSettings } from "../../../hooks/usePublicSettings.js";
import ConversationDetailTabs from "./ConversationDetailTabs.jsx";
import ConversationSyncStatusBar from "./ConversationSyncStatusBar.jsx";
import { useConversationDetail } from "../hooks/useConversationDetail.js";
import { closeConversation, handoffConversation, assignConversation, releaseConversation } from "../services/conversationListService.js";
import { lookupLeadByPhone } from "../services/leadLookupService.js";
import { getConversationLeadDraft } from "../services/conversationLeadDraftService.js";
import { generateConversationTakeoverMessage } from "../utils/conversationTakeoverMessages.js";
import {
  getConversationInboxStatusTag,
  getConversationHandoffStatusTag,
  getConversationLeadStatusTag,
} from "../utils/conversationLeadStatus.js";

const { Text } = Typography;
const { useBreakpoint } = Grid;
const TAB_BAR_HEIGHT = 36;

function useChatScrollHeight(containerRef, deps = []) {
  const [scrollHeight, setScrollHeight] = useState(null);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const update = () => {
      const { height } = node.getBoundingClientRect();
      const next = Math.floor(height - TAB_BAR_HEIGHT);
      if (next > 120) {
        setScrollHeight(next);
      }
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    window.addEventListener("resize", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, deps);

  return scrollHeight;
}

const TRANSCRIPT_LINE = /^\[([^\]]+)\]\s*\[([^\]]+)\]\s*([\s\S]*)$/;

/**
 * Parse a "[timestamp] [Speaker] message" transcript (with multi-line messages)
 * into structured chat messages for ConversationChatThread.
 */
export function parseTranscriptToMessages(transcript) {
  if (!transcript || typeof transcript !== "string") return [];

  const messages = [];
  let current = null;

  transcript.split("\n").forEach((rawLine) => {
    const match = rawLine.match(TRANSCRIPT_LINE);
    if (match) {
      if (current) messages.push(current);
      current = {
        index: messages.length,
        timestamp: match[1]?.trim() || null,
        role: match[2]?.trim() || null,
        text: (match[3] || "").trim(),
      };
    } else if (current) {
      current.text = `${current.text}\n${rawLine}`.trim();
    } else if (rawLine.trim()) {
      current = {
        index: 0,
        timestamp: null,
        role: messages.length % 2 === 0 ? "customer" : "agent",
        text: rawLine.trim(),
      };
    }
  });

  if (current) messages.push(current);
  return messages.filter((m) => m.text);
}

function buildConversationNotes(summary, phone, customerName) {
  const header = [
    "--- Converted from Paul ↔ customer WhatsApp conversation ---",
    customerName ? `Customer: ${customerName}` : null,
    `Phone: ${phone}`,
    "",
  ]
    .filter(Boolean)
    .join("\n");

  const body =
    summary?.fullChat?.transcript?.trim() ||
    summary?.text?.trim() ||
    "(No transcript captured)";

  return `${header}\n${body}`;
}

function StatusTags({
  messageCount,
  inboxStatusTag,
  handoffStatusTag,
  leadStatusTag,
  readyForLead,
}) {
  const tags = [];
  if (messageCount > 0) {
    tags.push(
      <Tag key="count" style={{ margin: 0 }}>
        {messageCount} messages
      </Tag>
    );
  }
  if (inboxStatusTag) {
    tags.push(
      <Tag key="inbox" color={inboxStatusTag.color} style={{ margin: 0 }}>
        {inboxStatusTag.label}
      </Tag>
    );
  }
  if (handoffStatusTag) {
    tags.push(
      <Tag key="handoff" color={handoffStatusTag.color} style={{ margin: 0 }}>
        {handoffStatusTag.label}
      </Tag>
    );
  }
  if (leadStatusTag) {
    tags.push(
      <Tag key="lead" color={leadStatusTag.color} style={{ margin: 0 }}>
        {leadStatusTag.label}
      </Tag>
    );
  }
  if (readyForLead) {
    tags.push(
      <Tag key="ready" color="success" style={{ margin: 0 }}>
        Ready to convert
      </Tag>
    );
  }
  if (tags.length === 0) return null;
  return (
    <Flex gap={4} wrap style={{ minWidth: 0, flex: 1 }}>
      {tags}
    </Flex>
  );
}

const ConversationDrawerPanel = ({
  phone,
  customerName,
  referenceNumber,
  leadId: leadIdProp = null,
  inboxStatus = "open",
  assignedTo: assignedToProp = null,
  handoffStatus: handoffStatusProp = null,
  draftReadyToPromote = false,
  draftStatus = null,
  onClose,
  onClosed,
  onHandoffChange,
}) => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  const { settings } = usePublicSettings();
  const [osLead, setOsLead] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [assignedTo, setAssignedTo] = useState(assignedToProp);
  const [handoffStatus, setHandoffStatus] = useState(handoffStatusProp);

  const {
    data,
    isLoading,
    isFetching,
    syncStatus,
    cachedAt,
    refresh,
  } = useConversationDetail(phone, { leadId: leadIdProp });

  const loading = isLoading || isFetching;

  useEffect(() => {
    setAssignedTo(assignedToProp);
    setHandoffStatus(handoffStatusProp);
  }, [assignedToProp, handoffStatusProp, phone]);
  const chatAreaRef = useRef(null);

  const loadOsLead = useCallback(async () => {
    if (!phone) return;
    setLookupLoading(true);
    try {
      const result = await lookupLeadByPhone(phone);
      setOsLead(result?.lead ?? null);
    } catch (err) {
      console.error("Error looking up lead by phone:", err);
      setOsLead(null);
    } finally {
      setLookupLoading(false);
    }
  }, [phone]);

  useEffect(() => {
    loadOsLead();
  }, [loadOsLead]);

  const summary = data?.summary || null;

  const messages = useMemo(() => {
    if (summary?.fullChat?.messages?.length) return summary.fullChat.messages;
    const transcript = summary?.fullChat?.transcript || summary?.text || "";
    return parseTranscriptToMessages(transcript);
  }, [summary]);

  const messageCount = summary?.messageCount ?? messages.length;
  const executiveSummary = summary?.text?.trim() || null;
  const linkedLeadId = leadIdProp || osLead?.leadId || null;
  const hasLinkedLead = Boolean(linkedLeadId);
  const isInboxClosed = inboxStatus === "closed";
  const inboxStatusTag = getConversationInboxStatusTag({ inboxStatus });
  const leadStatusTag = getConversationLeadStatusTag({
    leadId: hasLinkedLead ? linkedLeadId : null,
    draftReadyToPromote: hasLinkedLead ? false : draftReadyToPromote,
    draftStatus: hasLinkedLead ? null : draftStatus,
  });
  const readyForLead = !hasLinkedLead && draftReadyToPromote === true;

  const quoteResponseText =
    summary?.metadata?.quoteResponseText ||
    summary?.metadata?.quoteResponseIntent ||
    null;

  const chatMaxHeightFallback = isMobile
    ? "calc(100dvh - 170px)"
    : "calc(100vh - 190px)";

  const measuredChatHeight = useChatScrollHeight(chatAreaRef, [
    loading,
    Boolean(data),
    messages.length,
    Boolean(executiveSummary),
    isMobile,
  ]);

  const chatMaxHeight = measuredChatHeight
    ? `${measuredChatHeight}px`
    : chatMaxHeightFallback;

  const handleOpenLead = () => {
    if (!linkedLeadId) return;
    onClose?.();
    navigate(`/leads/${linkedLeadId}`);
  };

  const handleCloseConversation = () => {
    if (!phone || closeLoading || isInboxClosed) return;

    Modal.confirm({
      title: "Close conversation?",
      content:
        "This hides the thread from the default inbox. It reopens when the customer sends a new WhatsApp message.",
      okText: "Close",
      okButtonProps: { danger: true },
      onOk: async () => {
        setCloseLoading(true);
        try {
          await closeConversation({
            channelId: phone,
            leadId: linkedLeadId || undefined,
          });
          message.success("Conversation closed");
          onClosed?.();
          onClose?.();
        } catch (err) {
          console.error("Error closing conversation:", err);
          message.error("Failed to close conversation");
        } finally {
          setCloseLoading(false);
        }
      },
    });
  };

  const handleConvertToLead = async () => {
    if (!phone || convertLoading) return;

    setConvertLoading(true);
    const displayName = customerName || osLead?.name || "";
    const notes = buildConversationNotes(summary, phone, customerName);

    try {
      const resolution = await getConversationLeadDraft(phone, {
        customerName: displayName,
      });

      if (resolution?.action === "open_existing" && resolution.leadId) {
        onClose?.();
        navigate(`/leads/${resolution.leadId}`);
        return;
      }

      const fromConversation = {
        phone,
        customerName: displayName,
        notes,
        sourceType: "whatsapp",
      };

      if (resolution?.formPrefill) {
        fromConversation.formPrefill = resolution.formPrefill;
        fromConversation.draftAction = resolution.action;
        fromConversation.missingFields = resolution.missingFields || [];
      }

      onClose?.();
      navigate("/leads/new", { state: { fromConversation } });
    } catch (err) {
      console.error("Error resolving lead draft for convert:", err);
      onClose?.();
      navigate("/leads/new", {
        state: {
          fromConversation: {
            phone,
            customerName: displayName,
            notes,
            sourceType: "whatsapp",
          },
        },
      });
    } finally {
      setConvertLoading(false);
    }
  };

  const loadOpsChannels = useCallback(
    async () => loadOpsChannelsFromSettings(settings),
    [settings]
  );

  const opsName = user?.firstName?.trim() || "";
  const opsActor = user?.email || user?.id || user?.userId || "";

  const isAssignedToMe =
    Boolean(assignedTo && opsActor) &&
    assignedTo.toLowerCase() === opsActor.toLowerCase();
  const showAssignSelf = !isAssignedToMe && !isInboxClosed;
  const showRelease = Boolean(assignedTo) && !isInboxClosed;

  const handoffStatusTag = getConversationHandoffStatusTag({
    assignedTo,
    handoffStatus,
  });

  const applyHandoffResult = (result) => {
    const payload = result?.data ?? result;
    if (payload?.assignedTo !== undefined) {
      setAssignedTo(payload.assignedTo);
    }
    if (payload?.handoffStatus !== undefined) {
      setHandoffStatus(payload.handoffStatus);
    }
    onHandoffChange?.();
  };

  const handleAssignSelf = async () => {
    if (!phone || assignLoading || isInboxClosed) return;

    setAssignLoading(true);
    try {
      const useHandoff = handoffStatus !== "active";
      const result = useHandoff
        ? await handoffConversation({
            channelId: phone,
            leadId: linkedLeadId || undefined,
            assignSelf: true,
          })
        : await assignConversation({
            channelId: phone,
            leadId: linkedLeadId || undefined,
          });
      applyHandoffResult(result);
      message.success(useHandoff ? "Conversation handed off and assigned to you" : "Conversation assigned to you");
    } catch (err) {
      console.error("Error assigning conversation:", err);
      message.error("Failed to assign conversation");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRelease = async () => {
    if (!phone || releaseLoading || isInboxClosed) return;

    setReleaseLoading(true);
    try {
      const result = await releaseConversation({
        channelId: phone,
        leadId: linkedLeadId || undefined,
      });
      applyHandoffResult(result);
      message.success("Conversation released");
    } catch (err) {
      console.error("Error releasing conversation:", err);
      message.error("Failed to release conversation");
    } finally {
      setReleaseLoading(false);
    }
  };

  const buildTakeoverMessage = useCallback(
    () =>
      generateConversationTakeoverMessage({
        customerName: customerName || osLead?.name || "",
        opsName,
      }),
    [customerName, osLead?.name, opsName]
  );

  const handleRefresh = () => {
    refresh();
    loadOsLead();
  };

  const moreMenuItems = [
  {
    key: "refresh",
    label: "Refresh",
    icon: <ReloadOutlined />,
    onClick: handleRefresh,
    disabled: loading || lookupLoading,
  },
  ...(!isInboxClosed
    ? [
        {
          key: "close",
          label: "Close conversation",
          icon: <CloseCircleOutlined />,
          danger: true,
          onClick: handleCloseConversation,
          disabled: closeLoading || (loading && !data),
        },
      ]
    : []),
  ];

  return (
    <Flex
      vertical
      gap={8}
      style={{ width: "100%", flex: 1, minHeight: 0, height: "100%" }}
    >
      <Flex
        align="center"
        justify="space-between"
        gap={8}
        wrap="wrap"
        style={{
          flexShrink: 0,
          paddingBottom: 8,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <StatusTags
          messageCount={messageCount}
          inboxStatusTag={inboxStatusTag}
          handoffStatusTag={handoffStatusTag}
          leadStatusTag={leadStatusTag}
          readyForLead={readyForLead}
        />

        <Flex gap={6} align="center" style={{ flexShrink: 0 }} wrap="wrap">
          {hasLinkedLead ? (
            <Button type="primary" size="small" onClick={handleOpenLead}>
              Open lead
            </Button>
          ) : isMobile ? (
            <Tooltip title="Convert to lead">
              <Button
                type="primary"
                size="small"
                icon={<UserAddOutlined />}
                onClick={handleConvertToLead}
                disabled={loading || convertLoading}
                loading={convertLoading}
                aria-label="Convert to lead"
              />
            </Tooltip>
          ) : (
            <Button
              type="primary"
              size="small"
              icon={<UserAddOutlined />}
              onClick={handleConvertToLead}
              disabled={loading || convertLoading}
              loading={convertLoading}
            >
              Convert to lead
            </Button>
          )}

          {showAssignSelf &&
            (isMobile ? (
              <Tooltip title="Assign self">
                <Button
                  size="small"
                  icon={<UserAddOutlined />}
                  onClick={handleAssignSelf}
                  loading={assignLoading}
                  disabled={loading && !data}
                  aria-label="Assign self"
                />
              </Tooltip>
            ) : (
              <Button
                size="small"
                icon={<UserAddOutlined />}
                onClick={handleAssignSelf}
                loading={assignLoading}
                disabled={loading && !data}
              >
                Assign self
              </Button>
            ))}

          {showRelease &&
            (isMobile ? (
              <Tooltip title="Release">
                <Button
                  size="small"
                  icon={<UserDeleteOutlined />}
                  onClick={handleRelease}
                  loading={releaseLoading}
                  disabled={loading && !data}
                  aria-label="Release"
                />
              </Tooltip>
            ) : (
              <Button
                size="small"
                icon={<UserDeleteOutlined />}
                onClick={handleRelease}
                loading={releaseLoading}
                disabled={loading && !data}
              >
                Release
              </Button>
            ))}

          {phone && (
            <OutboundMessageButton
              loadChannels={loadOpsChannels}
              buildMessage={buildTakeoverMessage}
              recipientMode="fixed"
              recipientPhone={phone}
              storageKey={OPS_OUTBOUND_CHANNEL_STORAGE_KEY}
              buttonLabel={isMobile ? "" : "WhatsApp takeover"}
              modalTitle="WhatsApp takeover"
              modalDescription="Choose which business line you are replying as. Your choice is saved as the default for next time."
              channelListLabel="Reply as"
              emptyChannelsMessage="No ops WhatsApp lines configured. Add messaging.channels in Settings or contact.whatsappPhone."
              disabled={loading && !data}
              size="small"
            />
          )}

          <Dropdown
            menu={{ items: moreMenuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              size="small"
              icon={<MoreOutlined />}
              loading={loading || lookupLoading || closeLoading || assignLoading || releaseLoading}
              aria-label="More actions"
            />
          </Dropdown>
        </Flex>
      </Flex>

      <ConversationSyncStatusBar
        syncStatus={syncStatus}
        cachedAt={cachedAt}
        onRetry={handleRefresh}
        compact={isMobile}
      />

      {isLoading && !data ? (
        <Flex align="center" justify="center" gap={8} style={{ padding: "48px 0" }}>
          <Spin />
          <Text type="secondary">Loading conversation…</Text>
        </Flex>
      ) : (
        <Flex vertical gap={8} style={{ flex: 1, minHeight: 0 }}>
          {data?.n8nError && (
            <Alert
              type="warning"
              showIcon
              message={
                data.n8nError.code === "not_found"
                  ? "No Paul conversation yet"
                  : `Conversation unavailable (${data.n8nError.code}${
                      data.n8nError.httpStatus != null
                        ? ` · HTTP ${data.n8nError.httpStatus}`
                        : ""
                    })`
              }
              description={
                data.n8nError.hint ||
                (data.n8nError.code === "not_found"
                  ? "This number has no WhatsApp thread in n8n yet."
                  : "Check the n8n workflow logs.")
              }
            />
          )}

          {!data?.n8nError && messages.length === 0 && !executiveSummary && !isLoading && (
            <Alert
              type="info"
              showIcon
              message="No conversation content yet"
              description="Messages and summary will appear here once Paul has exchanged WhatsApp messages with this customer."
            />
          )}

          {(messages.length > 0 || executiveSummary) && (
            <div
              ref={chatAreaRef}
              style={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <ConversationDetailTabs
                messages={messages}
                summaryText={executiveSummary}
                summaryFormat={summary?.format}
                metadata={summary?.metadata}
                messageCount={messageCount}
                isMobile={isMobile}
                chatMaxHeight={chatMaxHeight}
                quoteResponseText={quoteResponseText}
              />
            </div>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default ConversationDrawerPanel;
